//#region imports
import { Injectable } from '@angular/core';
import { Constant } from 'app/core/common/constant';
import {
  LoggedUser,
  UserRole,
} from 'app/core/model/users/logged-user.modal';
import { OnBoard } from 'app/core/model/users/on-board.modal';
import { BehaviorSubject, Subject } from 'rxjs';

//#endregion imports

//#region internal imports

//#endregion internal imports

@Injectable({
  providedIn: 'root',
})
export class AppDataService {
  private _connectedUser: LoggedUser = null;
  private _navigateUrl: string;
  private _currentRole: string;
  private _plan_expired: boolean = false;
  private _applicationStage: OnBoard[];
  private _currencyOptions: any = { currencySymbol: '₹', displayCurrency: true, digits: '1.0-0', locale: 'en-IN', code: 'INR' };
  private _userPermission: any = null;
  private _current_app:any =null;

  private _role = new Subject<UserRole>();
  role$ = this._role.asObservable();

  private _userPermissionSubject = new BehaviorSubject<any>(null);
  userPermission$ = this._userPermissionSubject.asObservable();

  // Observable to track when user data is loaded
  private _userDataLoaded = new BehaviorSubject<boolean>(false);
  userDataLoaded$ = this._userDataLoaded.asObservable();

  constructor() {}

  changeRole(role: UserRole) {
    this._role.next(role);
  }

  //#region connected user
  // Memory-only storage for connected user (no localStorage for security)
  public get connectedUser(): LoggedUser {
    return this._connectedUser;
  }

  public set connectedUser(value: LoggedUser) {
    this._connectedUser = value;
    // Mark user data as loaded when user is set
    if (value) {
      this._userDataLoaded.next(true);
    }
  }

  // Check if user data is available
  public get isUserDataLoaded(): boolean {
    return this._connectedUser !== null;
  }

  // Clear user data (memory-only data)
  public clearUserData(): void {
    this._connectedUser = null;
    this._currencyOptions = { currencySymbol: '₹', displayCurrency: true, digits: '1.0-0', locale: 'en-IN', code: 'INR' };
    this._userPermission = null;
    this._userPermissionSubject.next(null);
    this._userDataLoaded.next(false);
  }

  // Full logout - clears both localStorage items and memory data
  public logout(): void {
    // Clear localStorage items (only token and currentRole are stored there)
    localStorage.removeItem(Constant.LocalStorageKey.Token);
    localStorage.removeItem(Constant.LocalStorageKey.CurrentRole);
    // Clear other non-sensitive localStorage items that may exist
    localStorage.removeItem(Constant.LocalStorageKey.MobileView);
    localStorage.removeItem('rbi_notice');
    localStorage.removeItem('lastActive');
    localStorage.removeItem('lastActivity');
    // Clear role IDs stored during session
    localStorage.removeItem('admin');
    localStorage.removeItem('manager');
    localStorage.removeItem('finance');
    localStorage.removeItem('employee');
    // Clear memory-based data
    this.clearUserData();
    this._currentRole = null;
    this._navigateUrl = null;
    // Signal other tabs about logout
    localStorage.setItem('logout', Date.now().toString());
  }

  //#endregion connected user

  //#region
  public get currentUserRole(): string {
    if (this._currentRole) {
      return this._currentRole;
    } else {
      this._currentRole = JSON.parse(
        localStorage.getItem(Constant.LocalStorageKey.CurrentRole)
      );
      return this._currentRole;
    }
  }

  public set currentUserRole(value: string) {
    this._currentRole = value;
    localStorage.setItem(
      Constant.LocalStorageKey.CurrentRole,
      JSON.stringify(this._currentRole)
    );
  }
  //#endregion

  //#region navigateurl
  public get navigateUrl() {
    if (this._navigateUrl) {
      return this._navigateUrl;
    } else {
      this._navigateUrl = JSON.parse(
        sessionStorage.getItem(Constant.LocalStorageKey.Url)
      );
      return this._navigateUrl;
    }
  }

  public set navigateUrl(value: string) {
    this._navigateUrl = value;
    sessionStorage.setItem(
      Constant.LocalStorageKey.Url,
      JSON.stringify(this._navigateUrl)
    );
  }
  //#endregion navigateurl

  //#region

  public get applicationStage() {
    if (this._applicationStage) {
      return this._applicationStage;
    } else {
      this._applicationStage = JSON.parse(
        sessionStorage.getItem(Constant.LocalStorageKey.ApplicationStage)
      );
      return this._applicationStage;
    }
  }

  public set applicationStage(value: OnBoard[]) {
    this._applicationStage = value;
    sessionStorage.setItem(
      Constant.LocalStorageKey.ApplicationStage,
      JSON.stringify(this._applicationStage)
    );
  }


  public get plan_expired() {
    if (this._plan_expired) {
      return this._plan_expired;
    } else {
      this._applicationStage = JSON.parse(
        sessionStorage.getItem('plan_expired')
      );
      return this._plan_expired;
    }
  }

  public set plan_expired(value: boolean) {
    this._plan_expired = value;
    sessionStorage.setItem(
      'plan_expired',
      JSON.stringify(this._plan_expired)
    );
  }


  //#endregion


  //#region Currency and Permission (Memory-only storage for security)

  // Memory-only storage for currency options (no localStorage for security)
  public get currencyOption() {
    return this._currencyOptions;
  }

  public set currencyOption(value: any) {
    this._currencyOptions = value;
  }

  // Memory-only storage for user permission (no localStorage for security)
  public get userPermission() {
    return this._userPermission;
  }

  public set userPermission(value: any) {
    this._userPermission = value;
    this._userPermissionSubject.next(value);
  }

  public get currentApp() {
    if (this._current_app) {
      return this._current_app;
    } else {
      this._current_app =
        JSON.parse(localStorage.getItem(Constant.LocalStorageKey.App)) ?? 'EMS';
      return this._current_app;
    }
  }

  public set currentApp(value: any) {
    this._current_app = value;
    localStorage.setItem(
      Constant.LocalStorageKey.App,
      JSON.stringify(this._current_app)
    );
  }
  //#endregion
}
